import { database } from './firebase';
import { ref, get, onValue, off, set } from 'firebase/database';

/**
 * Calculate win rate percentage
 * @param {number} wins - Number of wins
 * @param {number} losses - Number of losses
 * @param {number} draws - Number of draws
 * @returns {number} Win rate percentage (0-100, 1 decimal place)
 */
export const calculateWinRate = (wins, losses, draws) => {
  const total = wins + losses + draws;
  if (total === 0) return 0;
  return Math.round((wins / total) * 1000) / 10; // 1 decimal place
};

/**
 * Get statistics for a specific game type
 * @param {string} userId - User's Firebase UID
 * @param {string} gameType - Game type (e.g., 'tictactoe', 'rps', 'connect4', 'dotsandboxes')
 * @returns {Promise<Object>} Game stats
 */
export const getGameStats = async (userId, gameType) => {
  try {
    const statsRef = ref(database, `matchStats/${userId}/${gameType}`);
    const snapshot = await get(statsRef);

    if (!snapshot.exists()) {
      // Return zeroed stats for games not yet played
      return {
        wins: 0,
        losses: 0,
        draws: 0,
        gamesPlayed: 0,
        winRate: 0
      };
    }

    const data = snapshot.val();
    const wins = data.wins || 0;
    const losses = data.losses || 0;
    const draws = data.draws || 0;
    // Always calculate gamesPlayed from actual results to ensure accuracy
    const gamesPlayed = wins + losses + draws;
    const winRate = calculateWinRate(wins, losses, draws);

    return {
      wins,
      losses,
      draws,
      gamesPlayed,
      winRate
    };
  } catch (error) {
    console.error(`Error getting stats for ${gameType}:`, error);
    // Return zeroed stats on error
    return {
      wins: 0,
      losses: 0,
      draws: 0,
      gamesPlayed: 0,
      winRate: 0
    };
  }
};

/**
 * Get overall statistics across all games
 * @param {string} userId - User's Firebase UID
 * @returns {Promise<Object>} Overall stats
 */
export const getOverallStats = async (userId) => {
  try {
    const statsRef = ref(database, `matchStats/${userId}`);
    const snapshot = await get(statsRef);

    if (!snapshot.exists()) {
      // Return zeroed stats for new users
      return {
        totalGames: 0,
        totalWins: 0,
        totalLosses: 0,
        totalDraws: 0,
        winRate: 0
      };
    }

    const allStats = snapshot.val();
    let totalWins = 0;
    let totalLosses = 0;
    let totalDraws = 0;

    // Aggregate stats across all game types
    Object.values(allStats).forEach(gameStats => {
      totalWins += gameStats.wins || 0;
      totalLosses += gameStats.losses || 0;
      totalDraws += gameStats.draws || 0;
    });

    const totalGames = totalWins + totalLosses + totalDraws;
    const winRate = calculateWinRate(totalWins, totalLosses, totalDraws);

    return {
      totalGames,
      totalWins,
      totalLosses,
      totalDraws,
      winRate
    };
  } catch (error) {
    console.error('Error getting overall stats:', error);
    // Return zeroed stats on error
    return {
      totalGames: 0,
      totalWins: 0,
      totalLosses: 0,
      totalDraws: 0,
      winRate: 0
    };
  }
};

/**
 * Calculate head-to-head statistics against a specific opponent
 * @param {string} userId - User's Firebase UID
 * @param {string} opponentId - Opponent's Firebase UID
 * @returns {Promise<Array>} H2H stats per game type
 */
export const getHeadToHeadStats = async (userId, opponentId) => {
  try {
    const historyRef = ref(database, `gameHistory/${userId}`);
    const snapshot = await get(historyRef);

    if (!snapshot.exists()) {
      return [];
    }

    const history = snapshot.val();
    const h2hByGame = {};

    // Filter game history for games against this opponent
    Object.values(history).forEach(game => {
      if (game.opponentId === opponentId) {
        // Normalize old 'checkers' to 'americancheckers' for consistency
        let gameType = game.gameType;
        if (gameType === 'checkers') {
          gameType = 'americancheckers';
        }

        if (!h2hByGame[gameType]) {
          h2hByGame[gameType] = {
            gameType,
            wins: 0,
            losses: 0,
            draws: 0,
            totalGames: 0
          };
        }

        // Increment appropriate counter
        if (game.result === 'won') {
          h2hByGame[gameType].wins++;
        } else if (game.result === 'lost') {
          h2hByGame[gameType].losses++;
        } else if (game.result === 'draw') {
          h2hByGame[gameType].draws++;
        }

        h2hByGame[gameType].totalGames++;
      }
    });

    // Convert object to array and add win rates
    return Object.values(h2hByGame).map(stats => ({
      ...stats,
      winRate: calculateWinRate(stats.wins, stats.losses, stats.draws)
    }));
  } catch (error) {
    console.error('Error getting head-to-head stats:', error);
    return [];
  }
};

/**
 * Reset all stats for a user (ADMIN FUNCTION)
 * @param {string} userId - User's Firebase UID
 * @returns {Promise<boolean>} Success status
 */
export const resetAllStats = async (userId) => {
  try {
    const statsRef = ref(database, `matchStats/${userId}`);
    const historyRef = ref(database, `gameHistory/${userId}`);

    // Delete all stats
    await set(statsRef, null);

    // Delete all game history
    await set(historyRef, null);

    console.log('✅ Stats and game history have been reset successfully!');
    return true;
  } catch (error) {
    console.error('Error resetting stats:', error);
    return false;
  }
};

/**
 * Subscribe to real-time stats updates
 * @param {string} userId - User's Firebase UID
 * @param {Function} callback - Callback function called with stats data
 * @returns {Function} Unsubscribe function
 */
export const subscribeToStats = (userId, callback) => {
  const statsRef = ref(database, `matchStats/${userId}`);

  const handleStatsUpdate = async (snapshot) => {
    try {
      // Get overall stats
      let overall = {
        totalGames: 0,
        totalWins: 0,
        totalLosses: 0,
        totalDraws: 0,
        winRate: 0
      };

      const byGame = {};
      const gameTypes = ['tictactoe', 'rps', 'connect4', 'dotsandboxes', 'americancheckers', 'russiancheckers', 'battleship', 'chess'];

      if (snapshot.exists()) {
        const allStats = snapshot.val();

        // Calculate overall stats
        let totalWins = 0;
        let totalLosses = 0;
        let totalDraws = 0;

        Object.values(allStats).forEach(gameStats => {
          totalWins += gameStats.wins || 0;
          totalLosses += gameStats.losses || 0;
          totalDraws += gameStats.draws || 0;
        });

        overall = {
          totalGames: totalWins + totalLosses + totalDraws,
          totalWins,
          totalLosses,
          totalDraws,
          winRate: calculateWinRate(totalWins, totalLosses, totalDraws)
        };

        // Get stats for each game type
        for (const gameType of gameTypes) {
          // Merge old 'checkers' stats into 'americancheckers'
          if (gameType === 'americancheckers') {
            const americanStats = allStats['americancheckers'] || { wins: 0, losses: 0, draws: 0 };
            const legacyStats = allStats['checkers'] || { wins: 0, losses: 0, draws: 0 };

            const wins = (americanStats.wins || 0) + (legacyStats.wins || 0);
            const losses = (americanStats.losses || 0) + (legacyStats.losses || 0);
            const draws = (americanStats.draws || 0) + (legacyStats.draws || 0);

            byGame[gameType] = {
              wins,
              losses,
              draws,
              gamesPlayed: wins + losses + draws,
              winRate: calculateWinRate(wins, losses, draws)
            };
          } else if (allStats[gameType]) {
            const stats = allStats[gameType];
            const wins = stats.wins || 0;
            const losses = stats.losses || 0;
            const draws = stats.draws || 0;
            byGame[gameType] = {
              wins,
              losses,
              draws,
              // Always calculate gamesPlayed from actual results to ensure accuracy
              gamesPlayed: wins + losses + draws,
              winRate: calculateWinRate(wins, losses, draws)
            };
          } else {
            byGame[gameType] = {
              wins: 0,
              losses: 0,
              draws: 0,
              gamesPlayed: 0,
              winRate: 0
            };
          }
        }
      } else {
        // No stats yet, return zeros for all games
        for (const gameType of gameTypes) {
          byGame[gameType] = {
            wins: 0,
            losses: 0,
            draws: 0,
            gamesPlayed: 0,
            winRate: 0
          };
        }
      }

      callback({ overall, byGame });
    } catch (error) {
      console.error('Error processing stats update:', error);
    }
  };

  // Subscribe to changes
  onValue(statsRef, handleStatsUpdate);

  // Return unsubscribe function
  return () => {
    off(statsRef, 'value', handleStatsUpdate);
  };
};
